import {
  Instagram, Twitter, Linkedin, Youtube,
  MapPin, Mail, Phone,
  Home, Users, Layers, Sparkles, LayoutGrid,
  Megaphone, BookOpen, Star, TrendingUp,
} from "lucide-react";

const socials = [
  { icon: Instagram, href: "#", label: "Instagram" },
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Youtube, href: "#", label: "Youtube" },
];

const columns = [
  {
    title: "Contact",
    items: [
      { icon: MapPin, label: "Hyderabad, India" },
      { icon: Mail, label: "hello@affix.in", href: "mailto:hello@affix.in" },
      { icon: Phone, label: "+91 99897 68401" },
    ],
  },
  {
    title: "Pages",
    items: [
      { icon: Home, label: "Home", href: "#top" },
      { icon: Users, label: "Creator Network", href: "#creators" },
      { icon: LayoutGrid, label: "Campaigns", href: "#campaigns" },
      { icon: Sparkles, label: "Process", href: "#process" },
      { icon: Layers, label: "About", href: "#team" },
    ],
  },
  {
    title: "Services",
    items: [
      { icon: Megaphone, label: "Influencer Campaigns", href: "#services" },
      { icon: BookOpen, label: "Story-Led Campaigns", href: "#services" },
      { icon: Star, label: "Celebrity Endorsements", href: "#services" },
      { icon: TrendingUp, label: "Sequential Campaigns", href: "#services" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative isolate overflow-hidden bg-[#0B1C3D] text-white pt-20 md:pt-28"
    >
      {/* Decorative glows */}
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full blur-3xl opacity-30"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(37,99,235,0.35) 0%, transparent 70%)",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute top-40 right-10 w-[300px] h-[300px] rounded-full blur-3xl opacity-30"
        style={{
          background:
            "radial-gradient(circle, rgba(236,72,153,0.18) 0%, transparent 70%)",
        }}
        aria-hidden
      />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(ellipse at top, black 20%, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at top, black 20%, transparent 75%)",
        }}
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid lg:grid-cols-[1.25fr_1.4fr] gap-12 lg:gap-16">
          {/* LEFT — headline */}
          <div>
            <h2
              data-testid="footer-headline"
              className="font-heading font-bold text-[44px] sm:text-5xl lg:text-[60px] xl:text-[68px] leading-[1.05] tracking-tight text-white"
            >
              <span className="block whitespace-nowrap">Your next big</span>
              <span
                className="block whitespace-nowrap"
                style={{
                  background:
                    "linear-gradient(90deg, #60A5FA 0%, #3B82F6 35%, #2563EB 65%, #1E3A8A 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                }}
              >
                move
              </span>
              <span className="block whitespace-nowrap">starts here</span>
            </h2>

            <p
              data-testid="footer-tagline"
              className="mt-8 md:mt-10 font-serif-italic text-lg md:text-xl text-slate-300 max-w-md"
            >
              Let's build brands people can't ignore.
            </p>

            <div className="mt-8 flex items-center gap-3">
              {socials.map((s) => {
                const I = s.icon;
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    aria-label={s.label}
                    data-testid={`social-${s.label.toLowerCase()}`}
                    className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:text-white hover:border-blue-400 hover:bg-blue-500/15 transition-all"
                  >
                    <I className="w-[18px] h-[18px]" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* RIGHT — link columns */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-10">
            {columns.map((col, idx) => (
              <div
                key={col.title}
                data-testid={`footer-col-${col.title.toLowerCase()}`}
                className={`${
                  idx > 0 ? "sm:border-l sm:border-white/10 sm:pl-6 md:pl-8" : ""
                }`}
              >
                <div className="text-[11px] tracking-[0.3em] uppercase text-slate-400 font-semibold">
                  {col.title}
                </div>
                <ul className="mt-6 space-y-5">
                  {col.items.map((it) => {
                    const I = it.icon;
                    const content = (
                      <span className="inline-flex items-center gap-3 text-slate-300 hover:text-white transition-colors">
                        <span className="w-5 h-5 flex items-center justify-center text-slate-400 group-hover:text-blue-300 transition-colors">
                          <I className="w-4 h-4" />
                        </span>
                        <span className="text-[14px] md:text-[15px]">
                          {it.label}
                        </span>
                      </span>
                    );
                    return (
                      <li key={it.label}>
                        {it.href ? (
                          <a href={it.href} className="group inline-flex">
                            {content}
                          </a>
                        ) : (
                          <span className="group inline-flex">{content}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* GIANT affix watermark */}
      <div
        aria-hidden
        className="relative mt-8 md:mt-10 overflow-hidden select-none"
      >
        <div
          className="font-heading font-black text-center whitespace-nowrap px-2 pt-[0.2em] pb-[0.08em]"
          style={{
            fontSize: "clamp(120px, 30vw, 520px)",
            lineHeight: 1,
            letterSpacing: "0.04em",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 60%, rgba(11,28,61,0) 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
            color: "transparent",
          }}
        >
          affix
        </div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0B1C3D] via-transparent to-transparent" />
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-6 flex flex-wrap items-center justify-between gap-4 text-[12px] text-slate-400">
          <div>
            © {new Date().getFullYear()} Affix. Creator commerce, made regional.
          </div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white transition-colors">Privacy</a>
            <a href="#" className="hover:text-white transition-colors">Terms</a>
            <a href="#" className="hover:text-white transition-colors">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
